import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import UploadContextlyModal from "../components/UploadContextlyModal";
import Footer from "../components/Footer";
import "./MainPage.css";

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000";

const MainPage = () => {
  const [isModalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();

  const handleCreateClick = async () => {
    try {
      await axios.delete(`${BASE_URL}/sessions`);
    } catch (error) {
      // Ignore error for demo
    }
    try {
      const res = await axios.post(`${BASE_URL}/sessions`, { name: "Contextly Session" });
      sessionStorage.setItem("currentSessionId", res.data.session_id);
    } catch (error) {
      console.error("Failed to create session:", error);
    }
    setModalOpen(true);
  };

  const handleSubmit = (data) => {
    navigate("contextlyai", { state: data });
  };

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-badge">
            <span className="hero-badge-dot"></span>
            v2.0 NOW AVAILABLE
          </div>
          <h1 className="hero-title">
            Chat with your Data.<br />
            <span className="hero-title-gradient">Instantly.</span>
          </h1>
          <p className="hero-subtitle">
            Enterprise-grade RAG technology that turns your documents into actionable insights. Secure, citation-backed answers in milliseconds, not minutes.
          </p>
          <div className="hero-buttons">
            <button className="btn-primary" onClick={handleCreateClick}>
              Try Contextly Free <i className="fa fa-bolt"></i>
            </button>
            <Link to="/how-it-works" className="btn-secondary">
              <i className="fa fa-play-circle"></i> How it Works
            </Link>
          </div>
        </div>

        {/* Video Demo Area */}
        <div className="hero-demo">
          <div className="demo-window">
            <div className="demo-window-bar">
              <span className="demo-dot red"></span>
              <span className="demo-dot yellow"></span>
              <span className="demo-dot green"></span>
              <span className="demo-window-title">contextly-demo.mp4</span>
            </div>
            <div className="demo-window-content">
              <div className="demo-play-btn">
                <i className="fa fa-play"></i>
              </div>
              <div className="demo-particles">
                {[...Array(20)].map((_, i) => (
                  <div key={i} className="demo-particle" style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 3}s`,
                    animationDuration: `${3 + Math.random() * 4}s`
                  }}></div>
                ))}
              </div>
            </div>
            <div className="demo-progress">
              <div className="demo-progress-bar"></div>
            </div>
          </div>
        </div>
      </section>


      {/* Why Contextly */}
      <section className="why-section">
        <h2 className="section-title">Why Contextly?</h2>
        <p className="section-subtitle">
          Built for precision. Our advanced retrieval pipeline ensures your AI understands the context, not just the keywords.
        </p>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon blue">
              <i className="fa fa-map-pin"></i>
            </div>
            <h3>No Hallucinations</h3>
            <p>Our engine grounds every response in your actual data. If the answer isn't in your docs, the AI says so, eliminating dangerous guesswork.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon cyan">
              <i className="fa fa-bolt"></i>
            </div>
            <h3>Lightning Fast Ingestion</h3>
            <p>Process PDFs, Word docs, and web pages in seconds. Our vectorization pipeline handles 100+ pages instantly with optimized indexing.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon green">
              <i className="fa fa-lock"></i>
            </div>
            <h3>Privacy First</h3>
            <p>Your data stays yours. We never use your documents to train our models. All data is encrypted at rest and in transit.</p>
          </div>
        </div>
      </section>

      {/* Inside the Engine */}
      <section className="engine-section">
        <div className="engine-content">
          <div className="engine-text">
            <h2 className="engine-title">Inside the Engine</h2>
            <p className="engine-description">
              Contextly uses Retrieval-Augmented Generation (RAG) to bridge the gap between static documents and dynamic conversation.
            </p>
            <div className="engine-steps">
              <div className="engine-step">
                <span className="step-number">1</span>
                <div>
                  <h4>Vector Embedding</h4>
                  <p>Documents are chunked and converted into high-dimensional vector representations.</p>
                </div>
              </div>
              <div className="engine-step">
                <span className="step-number">2</span>
                <div>
                  <h4>Semantic Search</h4>
                  <p>User queries are matched against your knowledge base using cosine similarity, not just keywords.</p>
                </div>
              </div>
              <div className="engine-step">
                <span className="step-number">3</span>
                <div>
                  <h4>Contextual Synthesis</h4>
                  <p>Relevant snippets are fed to the LLM to generate precise, citation-backed answers.</p>
                </div>
              </div>
            </div>
          </div>
          <div className="engine-visual">
            <div className="engine-flow">
              <div className="flow-node">
                <i className="fa fa-file-alt"></i>
                <span>Store</span>
              </div>
              <div className="flow-connector"></div>
              <div className="flow-node engine-node">
                <i className="fa fa-cog"></i>
                <span>Engine</span>
              </div>
              <div className="flow-connector"></div>
              <div className="flow-node answer-node">
                <i className="fa fa-comment"></i>
                <span>Answer</span>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <section className="cta-section">
        <h2 className="cta-title">Ready to transform your workflow?</h2>
        <p className="cta-subtitle">Start chatting with your documents today.</p>
        <button className="btn-primary" onClick={handleCreateClick}>Start Free <i className="fa fa-bolt"></i></button>
      </section>

      <Footer />

      <UploadContextlyModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        existingSources={{ files: [], links: [], texts: [] }}
      />
    </div>
  );
};

export default MainPage;
